/**
 * =============================================================================
 * IDS CRM Web - return visits and follow-up tickets on a job (Phase 6 steps 4-5)
 * =============================================================================
 *
 * A finished job with robots left Not done can have return visits booked
 * against it: new jobs that carry return_visit_of_task_id. GET /tasks/:id
 * lists them as return_visits [{ task_id, task_ref, status, scheduled_date,
 * asset_ids }]. A robot counts as booked once it is on a return visit that
 * has not been cancelled: a return visit that has since finished still
 * covers it (if the robot was not done again, that new job offers its own
 * return visit).
 *
 * Shared by the job page (TaskDetail) and Schedule Engineer Work (TaskForm).
 * =============================================================================
 */

import { dateKeyOf } from './dates';

// Short month names written out: en-GB Intl prints September as "Sept".
const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** A calendar day as '2 Oct' (UK, no year); '' when there is no day. */
export function shortDayLabel(value) {
  const key = dateKeyOf(value);
  if (!key) return '';
  const [, month, day] = key.split('-').map(Number);
  return `${day} ${SHORT_MONTHS[month - 1]}`;
}

/** The return visits booked against a job that still count: every one not cancelled. */
export function liveReturnVisits(task) {
  const listed = Array.isArray(task?.return_visits) ? task.return_visits : [];
  return listed.filter(visit => visit && visit.task_id && visit.status !== 'cancelled');
}

/** The first live return visit a robot is on, or null. */
export function returnVisitCovering(task, assetId) {
  if (!assetId) return null;
  return liveReturnVisits(task)
    .find(visit => (Array.isArray(visit.asset_ids) ? visit.asset_ids : []).includes(assetId)) || null;
}

/** "Booked on JOB-2026-0070 for 2 Oct" (the day left off when there is none). */
export function bookedOnSentence(visit) {
  if (!visit) return '';
  const day = shortDayLabel(visit.scheduled_date);
  return `Booked on ${visit.task_ref || 'a return visit'}${day ? ` for ${day}` : ''}`;
}

/** A ticket status as people read it: 'in_progress' becomes 'In progress'. */
export function ticketStatusLabel(status) {
  if (!status) return '';
  const words = String(status).replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}
